/* eslint-disable react-hooks/exhaustive-deps */
import React, { createContext, ReactNode, useContext, useEffect } from 'react'
import usePayment, {
  PaymentMethod,
  Address,
  PaymentInfo,
  NewAddress,
  NewPaymentInfo
} from 'hooks/usePayment'
import { AuthContext } from 'contexts/AuthContext'

type PaymentContextType = {
  fetchPaymentMethods: () => Promise<void>
  savePaymentInfo: (newPaymentInfo: NewPaymentInfo) => Promise<void>
  updatePaymentMethod: (updatePaymentInfo: PaymentInfo) => Promise<void>
  deletePaymentMethod: (paymentProfileId: string) => Promise<void>
  addAddress: (newAddress: NewAddress) => Promise<void>
  updateAddress: (updateAddress: Address) => Promise<void>
  deleteAddress: (addressId: string) => Promise<void>
  fetchAddresses: () => Promise<void>
  paymentMethods: PaymentMethod[]
  addresses: Address[]
  isLoadingPaymentMethods: boolean
  isLoadingAddresses: boolean
}

const defaultPaymentContextValue: PaymentContextType = {
  fetchPaymentMethods: async () => {
    throw new Error('fetchPaymentMethods method not implemented')
  },
  savePaymentInfo: async () => {
    throw new Error('savePaymentInfo method not implemented')
  },
  updatePaymentMethod: async () => {
    throw new Error('updatePaymentMethod method not implemented')
  },
  deletePaymentMethod: async () => {
    throw new Error('deletePaymentMethod method not implemented')
  },
  addAddress: async () => {
    throw new Error('addAddress method not implemented')
  },
  updateAddress: async () => {
    throw new Error('updateAddress method not implemented')
  },
  deleteAddress: async () => {
    throw new Error('deleteAddress method not implemented')
  },
  fetchAddresses: async () => {
    throw new Error('fetchAddresses method not implemented')
  },
  paymentMethods: [],
  addresses: [],
  isLoadingPaymentMethods: true,
  isLoadingAddresses: true
}

export const PaymentContext = createContext<PaymentContextType>(
  defaultPaymentContextValue
)

export const PaymentProvider: React.FC<{ children: ReactNode }> = ({
  children
}) => {
  const {
    fetchPaymentMethods,
    savePaymentInfo,
    updatePaymentMethod,
    deletePaymentMethod,
    paymentMethods,
    addAddress,
    updateAddress,
    deleteAddress,
    fetchAddresses,
    addresses,
    isLoadingPaymentMethods,
    isLoadingAddresses
  } = usePayment()

  const { isAuthenticated } = useContext(AuthContext)
  // Payment data is refreshed when authentication changes.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (isAuthenticated) {
      fetchPaymentMethods()
      fetchAddresses()
    }
  }, [isAuthenticated])

  const contextValue: PaymentContextType = {
    fetchPaymentMethods,
    savePaymentInfo,
    updatePaymentMethod,
    deletePaymentMethod,
    paymentMethods,
    addAddress,
    updateAddress,
    deleteAddress,
    fetchAddresses,
    addresses,
    isLoadingPaymentMethods,
    isLoadingAddresses
  }

  return (
    <PaymentContext.Provider value={contextValue}>
      {children}
    </PaymentContext.Provider>
  )
}
